import { ClickAwayListener } from "@mui/base";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoList } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";
import {
  type ReplyMode,
  type UseReplyModeResult,
  replyModes,
} from "../hooks/use-reply-mode";

export const ReplyModeDropdown = memo(function ReplyModeDropdown({
  replyModeState,
}: {
  replyModeState: UseReplyModeResult;
}) {
  const { activeReplyAction, replyModeActions, setReplyMode } = replyModeState;

  const [open, setOpen] = useState(false);
  const [anchorRef, setAnchorRef] = useState<HTMLButtonElement | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();

  const items: RedoListItem<ReplyMode>[] = useMemo(
    () =>
      replyModes.map((mode) => ({
        id: mode,
        type: "text" as const,
        value: mode,
        text: replyModeActions[mode].title,
        leadingItem: replyModeActions[mode].Icon
          ? { type: "icon" as const, Icon: replyModeActions[mode].Icon! }
          : undefined,
      })),
    [replyModeActions],
  );

  return (
    <Flex p="sm" pb="none">
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <span>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={activeReplyAction.Icon}
            IconTrailing={ChevronDownIcon}
            onClick={() => setOpen((prev) => !prev)}
            ref={setAnchorRef}
            size="sm"
            text={activeReplyAction.title}
          />
          <Dropdown anchor={anchorRef} fitToAnchor={false} open={open}>
            <RedoList
              focusedIndex={focusedIndex}
              items={items}
              itemSelected={({ item }) => {
                setReplyMode(item.value);
                setOpen(false);
              }}
              refToListenTo={null}
              setFocusedIndex={setFocusedIndex}
              size="sm"
            />
          </Dropdown>
        </span>
      </ClickAwayListener>
    </Flex>
  );
});
